import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Eye, Heart, TrendingUp, Calendar, Globe2 } from 'lucide-react';
import { API } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import GeoMap from './GeoMap';

const VideoStats = ({ videoId, videoTitle, onClose }) => {
  const { lang } = useLang();
  const [stats, setStats] = useState(null);
  const [geo, setGeo] = useState([]);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/videos/${videoId}/stats?days=${days}`, { withCredentials: true }).catch(() => ({ data: null })),
      axios.get(`${API}/videos/${videoId}/geo-stats`, { withCredentials: true }).catch(() => ({ data: { countries: [] } }))
    ]).then(([s, g]) => {
      setStats(s.data);
      setGeo(g.data?.countries || []);
    }).finally(() => setLoading(false));
  }, [videoId, days]);

  const maxVal = stats ? Math.max(...stats.days.map(d => Math.max(d.views, d.reactions * 3)), 1) : 1;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />
      <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center"><TrendingUp className="w-4 h-4 text-white" /></div>
            <div>
              <div className="font-black text-neutral-900 text-[16px]">{lang === 'sv' ? 'Statistik' : 'الإحصائيات'}</div>
              <div className="text-[11.5px] text-neutral-500 truncate max-w-[280px]">{videoTitle}</div>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-neutral-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 overflow-y-auto">
          <div className="flex items-center gap-2 mb-6">
            {[7, 14, 30].map(d => (
              <button key={d} onClick={() => setDays(d)} className={`h-9 px-4 rounded-full text-[12.5px] font-bold ${days === d ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`}>
                {d} {lang === 'sv' ? 'dagar' : 'يوم'}
              </button>
            ))}
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-14"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
          ) : !stats ? (
            <div className="text-center text-neutral-500 py-10">Ingen data</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="p-4 rounded-2xl bg-gradient-to-br from-cyan-50 to-cyan-100 border border-cyan-200">
                  <div className="flex items-center gap-1.5 text-cyan-700 mb-1">
                    <Eye className="w-3.5 h-3.5" />
                    <span className="text-[10.5px] font-bold uppercase tracking-wider">{lang === 'sv' ? 'Visningar' : 'مشاهدات'}</span>
                  </div>
                  <div className="text-3xl font-black text-cyan-900 tabular-nums">{stats.total_views}</div>
                </div>
                <div className="p-4 rounded-2xl bg-gradient-to-br from-fuchsia-50 to-fuchsia-100 border border-fuchsia-200">
                  <div className="flex items-center gap-1.5 text-fuchsia-700 mb-1">
                    <Heart className="w-3.5 h-3.5" fill="currentColor" />
                    <span className="text-[10.5px] font-bold uppercase tracking-wider">{lang === 'sv' ? 'Reaktioner' : 'تفاعلات'}</span>
                  </div>
                  <div className="text-3xl font-black text-fuchsia-900 tabular-nums">{stats.total_reactions}</div>
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-neutral-50 border border-neutral-200">
                <div className="flex items-center gap-1.5 mb-4">
                  <Calendar className="w-3.5 h-3.5 text-neutral-600" />
                  <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-700">{lang === 'sv' ? 'Dagligt' : 'يومي'}</span>
                  <div className="flex-1" />
                  <span className="text-[11px] flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-cyan-500" /> {lang === 'sv' ? 'Visningar' : 'مشاهدات'}</span>
                  <span className="text-[11px] flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-fuchsia-500" /> {lang === 'sv' ? 'Reaktioner' : 'تفاعلات'}</span>
                </div>
                <div className="flex items-end gap-1.5 h-48">
                  {stats.days.map((d, i) => {
                    const vh = (d.views / maxVal) * 100;
                    const rh = (d.reactions * 3 / maxVal) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
                        <div className="flex-1 w-full flex items-end justify-center gap-0.5">
                          <div className="w-1/2 bg-cyan-500 hover:bg-cyan-400 rounded-t-sm transition-colors relative" style={{ height: `${vh}%`, minHeight: d.views > 0 ? '2px' : '0' }}>
                            {d.views > 0 && <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 text-[10px] font-bold bg-neutral-900 text-white rounded px-1.5 py-0.5 pointer-events-none">{d.views}</div>}
                          </div>
                          <div className="w-1/2 bg-fuchsia-500 hover:bg-fuchsia-400 rounded-t-sm transition-colors" style={{ height: `${rh}%`, minHeight: d.reactions > 0 ? '2px' : '0' }} />
                        </div>
                        <div className="text-[9px] text-neutral-500 font-semibold rotate-45 origin-top-left mt-1 whitespace-nowrap">
                          {new Date(d.date).toLocaleDateString(lang === 'sv' ? 'sv-SE' : 'ar-SA', { month: 'short', day: 'numeric' })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="mt-6 p-4 rounded-2xl bg-neutral-50 border border-neutral-200">
                <div className="flex items-center gap-1.5 mb-4">
                  <Globe2 className="w-3.5 h-3.5 text-neutral-600" />
                  <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-700">{lang === 'sv' ? 'Delningskarta' : 'خريطة المشاركة'}</span>
                </div>
                <GeoMap countries={geo} lang={lang} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoStats;
